import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global reveal-on-scroll observer.
 * Mount once at the app root. Any element with class "reveal-on-scroll"
 * will receive the "is-visible" class when it enters the viewport.
 * Re-binds on route changes so new pages get observed too.
 */
const RevealOnScroll = () => {
  const location = useLocation();

  useEffect(() => {
    // Auto-tag common editorial elements on each page
    const autoSelectors = [
      'section > div > h1',
      'section > div > h2',
      'section > div > p',
      'article',
      '[data-reveal]',
    ];

    // Clean up any lingering state from prior route
    document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
      if (!el.classList.contains('is-visible')) {
        el.classList.remove('reveal-on-scroll');
      }
    });

    // Tag newly mounted elements (defer so DOM is ready)
    const tagTimer = setTimeout(() => {
      autoSelectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el, i) => {
          if (!el.classList.contains('reveal-on-scroll') && !el.classList.contains('is-visible')) {
            el.classList.add('reveal-on-scroll');
            // stagger nicely
            el.style.transitionDelay = `${Math.min(i * 40, 240)}ms`;
          }
        });
      });
    }, 40);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    const bindTimer = setTimeout(() => {
      document.querySelectorAll('.reveal-on-scroll:not(.is-visible)').forEach((el) => io.observe(el));
    }, 80);

    return () => {
      clearTimeout(tagTimer);
      clearTimeout(bindTimer);
      io.disconnect();
    };
  }, [location.pathname]);

  return null;
};

export default RevealOnScroll;
